import { mkConfig, generateCsv, download } from "export-to-csv";
import {
  IExecutionReport,
  IOrderRestatement,
} from "@pulse-ui/codegen/generated_ts/drop_copy";
import {
  IBalanceReportAmts,
  IParties,
  ITradingPositionReportPosQtys,
} from "@pulse-ui/codegen/generated_ts/position_management";
import { IPortfolioMetadata } from "@pulse-ui/codegen/generated_ts/org_metadata";
import { IAccountResponse } from "@pulse-ui/types/formatters";
import { IDataRow } from "@pulse-ui/types/components/Table";
import { PartyRole } from "@pulse-ui/codegen/generated_ts/types";

export const debounce = (func: any, delay: number) => {
  let timeoutId: any;

  return function (this: any, ...args: any) {
    const context: any = this;

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

type GenericObject = Record<string, any>;
export const flattenObject = (
  obj: GenericObject,
  parentKey: string = "",
  res: GenericObject = {}
): GenericObject => {
  for (let key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const propName = parentKey ? `${parentKey}_${key}` : key;
      if (Array.isArray(obj[key])) {
        obj[key].forEach((item: GenericObject, index: number) => {
          flattenObject(item, `${propName}_${index}`, res);
        });
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }
  }
  return res;
};

export const exportExcel = (rows: any[], filename?: string) => {
  if (!rows || rows.length === 0) return;
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    filename: filename ?? "sample",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });

  const rowData = rows.map((row) => flattenObject(row.original));
  const csv = generateCsv(csvConfig)(rowData);
  download(csvConfig)(csv);
};

export const getDataTypeOfObject = (
  object:
    | IDataRow
    | IExecutionReport
    | IOrderRestatement
    | IBalanceReportAmts
    | IPortfolioMetadata
    | IAccountResponse
    | ITradingPositionReportPosQtys
    | IParties
): string => {
  let output = "UnknownType";

  switch (true) {
    case "exec_id" in object:
      output = "IExecutionReport";
      break;
    case "orders_snapshot" in object:
      output = "IOrderRestatement";
      break;
    case "amt_total" in object:
      output = "IBalanceReportAmts";
      break;
    case "amt_total" in object:
      output = "IBalanceReportAmts";
      break;
    case "portfolio_id" in object && "accounts" in object:
      output = "IPortfolioMetadata";
      break;
    case "account_id" in object && "is_active" in object:
      output = "IAccountResponse";
      break;
    case "pos_qtys" in object:
      output = "ITradingPositionReportPosQtys";
      break;
    case "parties" in object:
      output = "IParties";
      break;
    default:
      output = "UnknownType";
      break;
  }

  return output;
};

export const getCurrentIsoTimestamp = (): string => {
  const now = new Date();
  const datePart = now.toISOString().split(".")[0];
  const milliseconds = now.getMilliseconds().toString().padStart(3, "0");
  const nanoseconds = "000000000";

  return `${datePart}.${milliseconds}${nanoseconds.slice(
    milliseconds.length
  )}Z`;
};

const mapStringToPartyRole = (role: string): PartyRole | undefined => {
  switch (role) {
    case "executing_firm":
      return PartyRole.executing_firm;
    case "exchange":
      return PartyRole.exchange;
    case "customer_account":
      return PartyRole.customer_account;
    case "intermediary":
      return PartyRole.intermediary;
    case "account_wallet":
      return PartyRole.account_wallet;
    case "blockchain_wallet":
      return PartyRole.blockchain_wallet;
    default:
      return undefined;
  }
};

export const findAccountWallet = (
  parties: IParties[]
): IParties | undefined => {
  return parties.find(
    (party) =>
      mapStringToPartyRole(party.party_role as unknown as string) ===
      PartyRole.account_wallet
  );
};

export const findPartyExchange = (
  parties: IParties[]
): IParties | undefined => {
  return parties.find(
    (party) =>
      mapStringToPartyRole(party.party_role as unknown as string) ===
      PartyRole.exchange
  );
};

export const currencyFormatter = (params: any, sign: string) => {
  const value = parseFloat(params.value);

  if (isNaN(value)) return "-";

  const absValue = Math.abs(value).toFixed(2);
  const formatted = absValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return value < 0 ? `(${sign}${formatted})` : `${sign}${formatted}`;
};

export const numericFormatterNoPrecision = (params: any) => {
  const value = parseFloat(params.value);

  if (isNaN(value)) return "-";

  const absValue = Math.abs(value);
  const [integerPart, fractionalPart] = absValue
    .toFixed(Math.max(2, (value.toString().split(".")[1] || []).length))
    .split(".");

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );
  const formattedValue = fractionalPart
    ? `${formattedIntegerPart}.${fractionalPart}`
    : formattedIntegerPart;

  return value < 0 ? `(${formattedValue})` : formattedValue;
};

export const numericFormatterWithPrecision = (
  params: any,
  precision: number,
  suppressRounding: boolean = false
) => {
  const value = parseFloat(params.value);

  if (isNaN(value)) return "-";

  const absValue = Math.abs(value);
  let formattedValue: string;

  if (suppressRounding) {
    const factor = Math.pow(10, precision);
    const truncatedValue = Math.floor(absValue * factor) / factor;
    formattedValue = truncatedValue.toFixed(precision);
  } else {
    formattedValue = absValue.toFixed(precision);
  }

  const [integerPart, fractionalPart] = formattedValue.split(".");

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );
  const finalFormattedValue = fractionalPart
    ? `${formattedIntegerPart}.${fractionalPart}`
    : formattedIntegerPart;

  return value < 0 ? `(${finalFormattedValue})` : finalFormattedValue;
};

export const numericFormatterWholeNum = (params: any) => {
  const value = parseInt(params.value);

  if (isNaN(value)) return "-";

  const absValue = Math.abs(value);
  const [integerPart, _] = absValue
    .toFixed(Math.max(2, (value.toString().split(".")[1] || []).length))
    .split(".");

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );

  return value < 0 ? `(${formattedIntegerPart})` : formattedIntegerPart;
};

export const numericFormatterMillions = (params: any, currency: string) => {
  const value = parseFloat(params.value);

  if (isNaN(value)) return "-";

  const absValue = Math.abs(value) / 1000000;
  const formattedValue = absValue.toFixed(2);

  const [integerPart, fractionalPart] = formattedValue.split(".");

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );

  const result = `${formattedIntegerPart}.${fractionalPart}`;

  return value < 0 ? `(${currency}${result})` : `${currency}${result}`;
};

export const customNumAggFunc = (params: any, precision?: number) => {
  let total = 0;

  const allValuesAreDash = params.values.every((value: any) => value === "-");
  if (allValuesAreDash) {
    return "-";
  }

  params.values.forEach((value: any) => {
    if (!Number.isNaN(parseFloat(value))) total += parseFloat(value);
  });
  return precision ? total.toFixed(precision) : total;
};

export const customTextAggFunc = (params: any) => {
  const valuesSet = [...new Set(params.values)];

  if (valuesSet.length === 1) {
    return valuesSet[0];
  } else {
    return "-";
  }
};

export const customNumericSortFunc = (a: any, b: any) => {
  const isADash = a === "-";
  const isBDash = b === "-";

  if (isADash && isBDash) return 0;

  const parsedA = isADash ? -Infinity : parseFloat(a);
  const parsedB = isBDash ? -Infinity : parseFloat(b);

  return parsedA - parsedB;
};

export const customStatusAggFunc = (params: any) => {
  if (params.values.some((value: any) => value === "degraded")) {
    return "degraded";
  }

  return "";
};
